import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button, Collapse, Typography } from '@/shared/components/atoms';
import Actions from '@/shared/components/atoms/EditableExpandableField/Actions';
import { IEditableExpandableFieldProps } from '@/shared/components/atoms/EditableExpandableField/EditableExpandableField';

import Trash from '~/images/shared/Trash.svg';

/**
 * An expandable and editable form field used within a field array with append and remove functionality
 * @param fieldArrayName Name of the field array to be used for validation.
 * @param index The position of this field within the field array
 * @param length The total number of fields in the field array.
 * @param children input elements displayed when the field is expanded
 * @param collapsedContent header or summary for the collapsed view
 * @param handleAppend append a new field handler
 * @param handleRemove remove field handler
 * @param expanded controlled mode for expanded state
 * @param onEdit Callback triggered when the user initiates editing, with control over toggling edit mode
 * @param addFieldText text for the add button
 * @param editFieldText text for the edit header
 * @param addAnotherFieldText text for the add another button
 */

const EditableExpandableField: React.FC<IEditableExpandableFieldProps> = ({
  fieldArrayName,
  index,
  length,
  children,
  collapsedHeader,
  collapsedContent,
  addFieldText,
  editFieldText,
  addAnotherFieldText,
  handleRemove,
  handleAppend,
  onEdit,
  canAppend = true,
  canDelete = true,
  expanded,
  actionsDisabled,
  actionsDisabledTooltipMessage,
  actionsVariant,
}) => {
  const { t } = useTranslation();

  const { trigger, clearErrors } = useFormContext();

  const fields = `${fieldArrayName}.${index}`;

  const isLastField = index === length - 1;

  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleEdit = () => {
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(toggleEdit);
    } else toggleEdit();
  };

  const handleCollapse = async () => {
    const isValid = await trigger(fields);
    if (isValid) {
      setIsValid(isValid);
      setIsEditing(false);
      setIsExpanded(false);
    }
  };

  // validate fields on mount to determine the view. We validate instead of checking for values because optional fields might also be valid.
  const validateOnMount = async () => {
    const triggerResult = await trigger(fields);
    clearErrors(fields);
    setIsValid(triggerResult);
    setIsExpanded(!triggerResult);
  };

  useEffect(() => {
    validateOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // editing view
  return expanded || isExpanded ? (
    <>
      {index > 0 && (
        <div className='my-24 flex items-center gap-8 border-t-xs border-t-neutralVariant-90 pt-24'>
          <Typography weight='semiBold'>
            {isEditing ? editFieldText : addFieldText}
          </Typography>
          {canDelete && (
            <span className='cursor-pointer' onClick={handleRemove}>
              <Trash />
            </span>
          )}
        </div>
      )}
      <div className='flex flex-col gap-16'>
        {children}
        {!expanded && (
          <Button
            size='full'
            variant='text'
            className='h-56 !rounded-xl !bg-surface-20'
            onClick={handleCollapse}
          >
            {isEditing ? t('save-edits') : addFieldText}
          </Button>
        )}
      </div>
    </>
  ) : // collapsable summary view
  isValid ? (
    <>
      <Collapse
        containerClassName='border-xs border-outline-default mt-16'
        borderRadius='rounded-xl'
        clickableArea='arrow'
        open={collapsedContent ? undefined : false}
        arrowStyle={classNames(!collapsedContent && 'hidden')}
        bgColor='white'
        childrenPadding='p-0'
        header={
          <div className='flex flex-grow items-center justify-between'>
            {collapsedHeader}
            <div className={classNames(collapsedContent && 'px-16')}>
              <Actions
                id={index}
                variant={
                  actionsVariant || (collapsedContent ? 'tooltip' : 'icons')
                }
                handleEdit={handleEdit}
                handleRemove={handleRemove}
                showDelete={canDelete && length > 1}
                disabled={actionsDisabled}
                disabledMessage={actionsDisabledTooltipMessage}
              />
            </div>
          </div>
        }
      >
        {collapsedContent}
      </Collapse>
      {isLastField && canAppend && (
        <Button
          size='full'
          variant='text'
          className='mt-24 h-56 !rounded-xl border-xs border-outline-default !bg-surface-20 text-primary-default'
          onClick={handleAppend}
        >
          {addAnotherFieldText}
        </Button>
      )}
    </>
  ) : null;
};

export default EditableExpandableField;
